import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CATEGORIES, TECHNOLOGIES, INDIAN_STATES, LISTING_TYPES, PRICE_UNITS, CONNECTIVITY_TYPES } from "../lib/constants";
import { Upload, X } from "lucide-react";

export default function CreateListing() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    category: "project",
    listing_type: "sell",
    technology: "solar",
    state: "",
    capacity_mw: "",
    price_inr: "",
    price_unit: "lumpsum",
    description: "",
    connectivity_type: "",
    voltage_kv: "",
    land_area_acres: "",
    duration_months: "",
    start_date: "",
    end_date: "",
    image_paths: [],
    document_paths: [],
  });

  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));
  const upd = (k) => (e) => set(k)(e.target.value);

  const uploadFiles = async (files, type) => {
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      setForm((p) => ({ ...p, [type]: [...p[type], ...paths] }));
      toast.success(`${paths.length} file(s) uploaded`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ["capacity_mw", "price_inr", "voltage_kv", "land_area_acres"].forEach((k) => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      payload.duration_months = payload.duration_months === "" ? null : parseInt(payload.duration_months, 10);
      const { data } = await api.post("/listings", payload);
      toast.success(data.status === "pending" ? "Submitted — it will appear once an admin approves it" : "Listing posted");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const cat = form.category;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-10" data-testid="create-listing-page">
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-3xl text-primary tracking-tight">Post a new listing</CardTitle>
          <p className="text-sm text-muted-foreground">
            Share the opportunity. It goes live once our admin team approves it.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SelectField label="Category" value={form.category} onChange={set("category")} options={CATEGORIES} testid="field-category" />
              <SelectField label="Listing type" value={form.listing_type} onChange={set("listing_type")} options={LISTING_TYPES} testid="field-type" />
            </div>

            <div className="space-y-2">
              <Label>Title</Label>
              <Input data-testid="field-title" value={form.title} onChange={upd("title")} required placeholder="e.g. 250 MW operational solar park, Rajasthan" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(cat === "project" || cat === "power") && (
                <SelectField label="Technology" value={form.technology} onChange={set("technology")} options={TECHNOLOGIES} testid="field-tech" />
              )}
              <div className="space-y-2">
                <Label>State</Label>
                <Select value={form.state} onValueChange={set("state")}>
                  <SelectTrigger data-testid="field-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {(cat === "project" || cat === "power" || cat === "connectivity") && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Capacity (MW)</Label>
                  <Input type="number" step="0.01" data-testid="field-capacity" value={form.capacity_mw} onChange={upd("capacity_mw")} />
                </div>
                {cat === "connectivity" && (
                  <>
                    <div className="space-y-2">
                      <Label>Connectivity type</Label>
                      <Select value={form.connectivity_type} onValueChange={set("connectivity_type")}>
                        <SelectTrigger data-testid="field-conn"><SelectValue placeholder="CTU / STU" /></SelectTrigger>
                        <SelectContent>
                          {CONNECTIVITY_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Voltage (kV)</Label>
                      <Input type="number" step="0.1" data-testid="field-voltage" value={form.voltage_kv} onChange={upd("voltage_kv")} />
                    </div>
                  </>
                )}
              </div>
            )}

            {cat === "land" && (
              <div className="space-y-2">
                <Label>Land area (acres)</Label>
                <Input type="number" step="0.1" data-testid="field-acres" value={form.land_area_acres} onChange={upd("land_area_acres")} />
              </div>
            )}

            {cat === "power" && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Duration (months)</Label>
                  <Input type="number" data-testid="field-duration" value={form.duration_months} onChange={upd("duration_months")} />
                </div>
                <div className="space-y-2">
                  <Label>Start date</Label>
                  <Input type="date" value={form.start_date} onChange={upd("start_date")} />
                </div>
                <div className="space-y-2">
                  <Label>End date</Label>
                  <Input type="date" value={form.end_date} onChange={upd("end_date")} />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Price (INR)</Label>
                <Input type="number" step="1" data-testid="field-price" value={form.price_inr} onChange={upd("price_inr")} />
              </div>
              <SelectField label="Price unit" value={form.price_unit} onChange={set("price_unit")} options={PRICE_UNITS} testid="field-price-unit" />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea rows={5} data-testid="field-desc" value={form.description} onChange={upd("description")} placeholder="Technical details, PPA status, offtake terms…" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FileUpload
                label="Images"
                paths={form.image_paths}
                onFiles={(fs) => uploadFiles(fs, "image_paths")}
                onRemove={(i) => setForm((p) => ({ ...p, image_paths: p.image_paths.filter((_, idx) => idx !== i) }))}
                accept="image/*"
                testid="field-images"
                uploading={uploading}
              />
              <FileUpload
                label="Documents"
                paths={form.document_paths}
                onFiles={(fs) => uploadFiles(fs, "document_paths")}
                onRemove={(i) => setForm((p) => ({ ...p, document_paths: p.document_paths.filter((_, idx) => idx !== i) }))}
                accept=".pdf,.docx,.xlsx,.doc,.xls"
                testid="field-docs"
                uploading={uploading}
              />
            </div>

            <Button type="submit" disabled={saving || uploading} className="w-full bg-primary hover:bg-primary/90" data-testid="create-listing-submit">
              {saving ? "Posting…" : "Post listing"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function FileUpload({ label, paths, onFiles, onRemove, accept, testid, uploading }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <label className="block border border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary transition-colors">
        <Upload className="h-5 w-5 mx-auto text-muted-foreground" />
        <div className="text-xs text-muted-foreground mt-1">{uploading ? "Uploading…" : "Click to upload"}</div>
        <input
          type="file"
          multiple
          accept={accept}
          data-testid={testid}
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) onFiles(Array.from(e.target.files));
            e.target.value = "";
          }}
        />
      </label>
      {paths.length > 0 && (
        <ul className="text-xs space-y-1">
          {paths.map((p, i) => (
            <li key={p} className="flex items-center justify-between bg-secondary rounded px-2 py-1">
              <span className="truncate">{p.split("/").pop()}</span>
              <button type="button" onClick={() => onRemove(i)} className="text-destructive"><X className="h-3 w-3" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
